import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Select, SearchInput, SegmentedControl, Button } from '../ds/index'
import { useApi } from '../lib/useApi'

export type Opt = { id: string; name: string; active?: boolean }
export type FilterOptions = {
  categories: Opt[]; areas: Opt[]; groups: Opt[]; reps: Opt[]
  buyers: (Opt & { category_id: string; status: string })[]
}

export const FILTER_KEYS = ['days', 'category', 'area', 'group', 'rep', 'buyer', 'q'] as const
type Key = typeof FILTER_KEYS[number]

export type DaysChoice = '1' | '7' | '30' | '90' | 'all'
const DAYS: { value: DaysChoice; label: string }[] = [
  { value: '1', label: 'Today' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: 'all', label: 'All' },
]

/**
 * Filters live in the address, so a filtered view can be bookmarked or sent to
 * someone, and moving between the board, categories and activity keeps them.
 */
export function useLeadFilters(defaultDays: DaysChoice) {
  const [params, setParams] = useSearchParams()
  const get = (k: Key) => params.get(k) || ''
  const days = (get('days') || defaultDays) as DaysChoice
  const set = (k: Key, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v); else next.delete(k)
    setParams(next, { replace: true })
  }
  const clear = () => {
    const next = new URLSearchParams(params)
    for (const k of FILTER_KEYS) if (k !== 'days') next.delete(k)
    setParams(next, { replace: true })
  }
  /** The filters as a query string for the API, always with days. */
  const query = (() => {
    const q = new URLSearchParams()
    q.set('days', days)
    for (const k of FILTER_KEYS) if (k !== 'days' && get(k)) q.set(k, get(k))
    return q.toString()
  })()
  /** The filters as a query string for a link to another screen. */
  const carry = (extra: Record<string, string> = {}) => {
    const q = new URLSearchParams()
    for (const k of FILTER_KEYS) if (get(k)) q.set(k, get(k))
    for (const [k, v] of Object.entries(extra)) if (v) q.set(k, v); else q.delete(k)
    const s = q.toString()
    return s ? `?${s}` : ''
  }
  const active = FILTER_KEYS.filter((k) => k !== 'days' && get(k)).length
  return { get, set, clear, days, query, carry, active, params }
}

export function useFilterOptions() {
  return useApi<FilterOptions>('/api/lead-filter-options').data
}

/** The filter strip. Pass `only` to show a subset. */
export function LeadFilterBar({ f, options, only, daysChoices = true }: {
  f: ReturnType<typeof useLeadFilters>
  options: FilterOptions | null
  only?: Key[]
  daysChoices?: boolean
}) {
  const show = (k: Key) => !only || only.includes(k)
  // Typing waits a moment before it narrows the list, so each key press is not a request.
  const [text, setText] = useState(f.get('q'))
  useEffect(() => { setText(f.get('q')) }, [f.get('q')]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (text === f.get('q')) return
    const t = setTimeout(() => f.set('q', text.trim()), 350)
    return () => clearTimeout(t)
  }, [text]) // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (k: Key, label: string, list: Opt[] | undefined, none?: string) => show(k) && (
    <Select aria-label={label} value={f.get(k)} onChange={(e) => f.set(k, e.target.value)} className={f.get(k) ? 'is-set' : undefined}>
      <option value="">{label}: all</option>
      {none && <option value="none">{none}</option>}
      {(list || []).map((o) => <option key={o.id} value={o.id}>{o.name}{o.active === false ? ' (inactive)' : ''}</option>)}
    </Select>
  )
  const buyers = (options?.buyers || []).filter((b) => !f.get('category') || f.get('category') === 'none' || b.category_id === f.get('category'))

  return (
    <div className="lead-filters">
      {daysChoices && show('days') && <SegmentedControl<DaysChoice> segments={DAYS} value={f.days} onChange={(v) => f.set('days', v)} />}
      <div className="lead-filters-row">
        {show('q') && <SearchInput aria-label="Search" placeholder="Search requests, groups, places" value={text} onChange={(e) => setText(e.target.value)} />}
        {pick('category', 'Category', options?.categories, 'No category')}
        {pick('area', 'Area', options?.areas, 'Area unclear')}
        {pick('group', 'Group', options?.groups)}
        {pick('rep', 'Rep', options?.reps)}
        {pick('buyer', 'Buyer', buyers.map((b) => ({ ...b, active: b.status === 'active' })))}
        {f.active > 0 && <Button variant="ghost" onClick={f.clear}>Clear {f.active} filter{f.active === 1 ? '' : 's'}</Button>}
      </div>
    </div>
  )
}
